import { useEffect, useState } from 'react';
import { Check, Save } from 'lucide-react';
import { Role, canActAs } from '@jeket-cloud/types/auth';
import { useUserRoles } from '@/contexts/AuthContext';
import { cn } from '@/lib/utils';
import { useProcessingSettings, useUpdateProcessingSettings } from '@/hooks/useProcessing';
import type { ProcessingSettings } from '@/types/processing';
import { Card, ErrorState, InlineError, LoadingState, PageHeader, buttonPrimary, inputClass } from './shared';

const ASPECT_OPTIONS: { value: string; label: string }[] = [
  { value: '9:16', label: '9:16 — Reels, TikTok, Shorts' },
  { value: '1:1', label: '1:1 — Instagram, LinkedIn' },
  { value: '4:5', label: '4:5 — Instagram portrait' },
  { value: '16:9', label: '16:9 — YouTube, X' },
];

const MIN_RETENTION_DAYS = 1;
const MAX_RETENTION_DAYS = 3650;

function Toggle({ label, hint, checked, disabled, onChange }: { label: string; hint: string; checked: boolean; disabled: boolean; onChange: (v: boolean) => void }) {
  return (
    <label className={cn('flex items-start justify-between gap-4 py-4', disabled ? 'cursor-default' : 'cursor-pointer')}>
      <span>
        <span className="block text-sm text-jeket-silver-bright">{label}</span>
        <span className="block text-xs text-jeket-silver-muted mt-0.5">{hint}</span>
      </span>
      <input
        type="checkbox"
        role="switch"
        aria-label={label}
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
        className="mt-1 h-4 w-4 accent-jeket-gold"
      />
    </label>
  );
}

export default function ProcessingSettingsPage() {
  const { data, isLoading, isError, error } = useProcessingSettings();
  const save = useUpdateProcessingSettings();
  const canEdit = canActAs(useUserRoles(), Role.SiteOperator);
  const [draft, setDraft] = useState<ProcessingSettings | null>(null);
  const [retentionText, setRetentionText] = useState('');

  useEffect(() => {
    if (data) {
      setDraft(data);
      setRetentionText(data.retentionDays == null ? '' : String(data.retentionDays));
    }
  }, [data]);

  if (isLoading) return <div className="p-6"><LoadingState label="Loading settings…" /></div>;
  if (isError || !draft) {
    return (
      <div className="p-4 sm:p-6 max-w-3xl mx-auto">
        <PageHeader title="Processing settings" />
        <ErrorState title="Couldn't load processing settings" error={error} />
      </div>
    );
  }

  // Empty = the service default (retentionDays: null).
  const useDefault = retentionText.trim() === '';
  const retention = Number(retentionText);
  const retentionValid =
    useDefault || (Number.isInteger(retention) && retention >= MIN_RETENTION_DAYS && retention <= MAX_RETENTION_DAYS);
  const next: ProcessingSettings = {
    ...draft,
    retentionDays: useDefault ? null : retentionValid ? retention : draft.retentionDays,
  };
  const dirty = JSON.stringify(next) !== JSON.stringify(data);

  const toggleAspect = (a: string) =>
    setDraft({
      ...draft,
      socialAspects: draft.socialAspects.includes(a) ? draft.socialAspects.filter((x) => x !== a) : [...draft.socialAspects, a],
    });

  return (
    <div className="p-4 sm:p-6 max-w-3xl mx-auto">
      <PageHeader
        title="Processing settings"
        subtitle={canEdit ? 'Applies to every recording in this organization.' : 'Read-only: changing these needs the Site Operator role.'}
      />

      <Card className="px-4 sm:px-6 divide-y divide-jeket-gold/10">
        <Toggle
          label="Auto social formats"
          hint="Render the social aspect ratios below as soon as a clip is cut."
          checked={draft.autoSocialFormat}
          disabled={!canEdit}
          onChange={(v) => setDraft({ ...draft, autoSocialFormat: v })}
        />

        <fieldset className="py-4" disabled={!canEdit}>
          <legend className="text-sm text-jeket-silver-bright">Social aspect ratios</legend>
          <p className="text-xs text-jeket-silver-muted mt-0.5 mb-3">Reframed renditions made for each clip.</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {ASPECT_OPTIONS.map((o) => {
              const on = draft.socialAspects.includes(o.value);
              return (
                <label key={o.value} className={cn('flex items-center gap-3 p-2 rounded-lg border text-sm', on ? 'border-jeket-gold/40 bg-jeket-gold/10 text-jeket-silver-bright' : 'border-jeket-gold/10 text-jeket-silver-muted')}>
                  <input type="checkbox" checked={on} onChange={() => toggleAspect(o.value)} className="accent-jeket-gold" />
                  {o.label}
                </label>
              );
            })}
          </div>
        </fieldset>

        <div className="py-4">
          <label htmlFor="retention-days" className="block text-sm text-jeket-silver-bright">Recording retention</label>
          <p className="text-xs text-jeket-silver-muted mt-0.5 mb-2">
            Programme media older than this is deleted. Clips keep their own copy and are not affected.
            Leave empty for the service default.
          </p>
          <div className="flex items-center gap-2">
            <input
              id="retention-days"
              type="number"
              min={MIN_RETENTION_DAYS}
              max={MAX_RETENTION_DAYS}
              value={retentionText}
              placeholder="Default"
              disabled={!canEdit}
              onChange={(e) => setRetentionText(e.target.value)}
              className={cn(inputClass, 'w-28')}
            />
            <span className="text-sm text-jeket-silver-muted">days</span>
          </div>
          {!retentionValid && (
            <p className="text-xs text-jeket-error mt-1">Enter a whole number of days between {MIN_RETENTION_DAYS} and {MAX_RETENTION_DAYS}.</p>
          )}
        </div>

        <Toggle
          label="LLM post-processing"
          hint="Run the final whole-event summary, show notes and chapters when an event ends."
          checked={draft.llmEnabled}
          disabled={!canEdit}
          onChange={(v) => setDraft({ ...draft, llmEnabled: v })}
        />
      </Card>

      {canEdit && (
        <div className="flex items-center justify-end gap-3 mt-4">
          {save.isSuccess && !dirty && (
            <span className="flex items-center gap-1 text-xs text-jeket-healthy"><Check size={12} /> Saved</span>
          )}
          <InlineError error={save.error} />
          <button type="button" onClick={() => save.mutate(next)} disabled={!dirty || !retentionValid || save.isPending} className={buttonPrimary}>
            <Save size={14} /> {save.isPending ? 'Saving…' : 'Save'}
          </button>
        </div>
      )}
    </div>
  );
}
